import React, { useState, useEffect, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Flex,
  Grid,
  GridItem,
  Card,
  Badge,
  Icon,
  Tabs,
  Table,
  useDisclosure,
  Input,
  NativeSelect,
  Spinner,
  Stat,
  Alert,
  Image,
  Link,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, Thead, Tbody, Tr, Th, Td } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import {
  FaUsers,
  FaMusic,
  FaChurch,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaEye,
  FaSignOutAlt,
  FaCalendarAlt,
  FaShieldAlt,
  FaEuroSign,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import AdminPlanesHub from './AdminPlanesHub';

const SOCIAL_NETWORK_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  twitter: 'X',
  youtube: 'YouTube',
};

function labelSocialNetwork(value) {
  if (!value) return '-';
  const key = String(value).toLowerCase();
  return SOCIAL_NETWORK_LABELS[key] || value;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const { open: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  
  const [stats, setStats] = useState(null);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [allVerifications, setAllVerifications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminVerificationCode, setAdminVerificationCode] = useState('');
  const [adminSocialUsername, setAdminSocialUsername] = useState('');
  const [adminSocialNetwork, setAdminSocialNetwork] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [statsRes, pendingRes, allVerRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getPendingVerifications(),
        adminAPI.getAllVerifications(),
        adminAPI.getAllUsers(),
      ]);
      
      setStats(statsRes.data);
      setPendingVerifications(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setAllVerifications(Array.isArray(allVerRes.data) ? allVerRes.data : []);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleViewDetails = async (request) => {
    try {
      const response = await adminAPI.getVerificationDetails(request.id);
      setSelectedRequest(response.data);
      setRejectionReason('');
      onOpen();
    } catch (error) {
      console.error('Error loading details:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudieron cargar los detalles',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handleProcessVerification = async (action, codeOverride) => {
    if (!selectedRequest) return;
    
    const effectiveCode = codeOverride || adminVerificationCode;

    let rejectReason = '';
    if (action === 'REJECT') {
      const reason = window.prompt('Indica el motivo de rechazo:');
      if (reason === null) return; // User cancelled
      if (!reason.trim()) {
        toaster.create({
          title: 'Error',
          description: 'Debes indicar un motivo de rechazo',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      rejectReason = reason;
    }

    if (action === 'APPROVE') {
      if (!effectiveCode.trim()) {
        toaster.create({
          title: 'Error',
          description: 'Debes introducir el código de validación',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      if (!adminSocialUsername.trim()) {
        toaster.create({
          title: 'Error',
          description: 'Debes introducir el nombre de usuario de la red social',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      if (!adminSocialNetwork) {
        toaster.create({
          title: 'Error',
          description: 'Debes seleccionar la red social desde la que envió el código',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
    }

    try {
      setIsProcessing(true);
      await adminAPI.processVerification(selectedRequest.id, {
        action,
        rejectionReason: action === 'REJECT' ? rejectReason : undefined,
        verificationCode: action === 'APPROVE' ? effectiveCode : undefined,
        socialUsername: action === 'APPROVE' ? adminSocialUsername : undefined,
        socialNetwork: action === 'APPROVE' ? adminSocialNetwork : undefined,
      });
      
      toaster.create({
        title: action === 'APPROVE' ? 'Perfil validado' : 'Solicitud rechazada',
        description: action === 'APPROVE' ? `Username asignado: @${adminSocialUsername.replace('@', '')}` : 'El perfil ha sido desbloqueado para que el usuario pueda corregir los datos.',
        type: 'success',
        duration: 3000,
      });
      
      setAdminVerificationCode('');
      setAdminSocialUsername('');
      setAdminSocialNetwork('');
      setRejectionReason('');
      setSearchResult(null);
      setSearchCode('');
      onClose();
      loadData();
    } catch (error) {
      console.error('Error processing verification:', error);
      const errorMsg = error.response?.data?.message || 'No se pudo procesar la solicitud';
      toaster.create({
        title: 'Error',
        description: errorMsg,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSearchByCode = async () => {
    if (!searchCode.trim() || searchCode.trim().length !== 6) {
      setSearchError('Introduce un código de 6 cifras');
      setSearchResult(null);
      return;
    }
    try {
      setIsSearching(true);
      setSearchError('');
      const response = await adminAPI.searchByCode(searchCode.trim());
      setSearchResult(response.data);
      setSelectedRequest(response.data);
      setAdminVerificationCode('');
      setAdminSocialUsername('');
    } catch (error) {
      setSearchResult(null);
      setSearchError(error.response?.data?.message || 'No se encontró ninguna solicitud con ese código');
    } finally {
      setIsSearching(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewUserProfile = async (userEntry) => {
    try {
      const response = await adminAPI.getUserProfile(userEntry.id, userEntry.userType);
      setSelectedUserProfile(response.data);
      onProfileOpen();
    } catch (error) {
      console.error('Error loading user profile:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudo cargar el perfil del usuario',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'yellow', text: 'Pendiente', icon: FaClock },
      VERIFIED: { color: 'green', text: 'Validado', icon: FaCheckCircle },
      REJECTED: { color: 'red', text: 'Rechazado', icon: FaTimesCircle },
      NOT_REQUESTED: { color: 'gray', text: 'No solicitado', icon: null },
    };
    
    const config = statusConfig[status] || statusConfig.NOT_REQUESTED;
    
    return (
      <Badge colorPalette={config.color} display="flex" alignItems="center" gap={1}>
        {config.icon && <Icon asChild><config.icon /></Icon>}
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" bg={bgColor}>
        <VStack gap={4}>
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
          <Text>Cargando panel de administración...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={4} px={6}>
        <Flex justify="space-between" align="center" maxW="1400px" mx="auto">
          <HStack gap={4}>
            <Icon boxSize={8} color="purple.500" asChild><FaShieldAlt /></Icon>
            <VStack align="start" gap={0}>
              <Heading size="md" color="purple.600">Panel de administración</Heading>
              <Text fontSize="sm" color="gray.500">Cadencya</Text>
            </VStack>
          </HStack>
          
          <HStack gap={4}>
            <Text fontSize="sm" color="gray.600">
              Hola, {user?.email}
            </Text>
            <Button colorPalette="gray" variant="ghost" size="sm" onClick={handleLogout}><FaSignOutAlt />Cerrar sesión
                          </Button>
          </HStack>
        </Flex>
      </Box>

      <Container maxW="1400px" py={8}>
        {/* Stats Grid */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
          <GridItem>
            <Card.Root bg={cardBg}>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label color="gray.500">Total Usuarios</Stat.Label>
                  <Stat.ValueText fontSize="3xl" color="purple.600">
                    {stats?.totalUsers || 0}
                  </Stat.ValueText>
                  <Stat.HelpText>
                    <Icon mr={1} asChild><FaUsers /></Icon>
                    Bandas y Cofradías
                  </Stat.HelpText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
          
          <GridItem>
            <Card.Root bg={cardBg}>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label color="gray.500">Bandas</Stat.Label>
                  <Stat.ValueText fontSize="3xl" color="blue.500">
                    {stats?.totalBandas || 0}
                  </Stat.ValueText>
                  <Stat.HelpText>
                    <Icon mr={1} asChild><FaMusic /></Icon>
                    {stats?.verifiedBandas || 0} verificadas
                  </Stat.HelpText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
          
          <GridItem>
            <Card.Root bg={cardBg}>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label color="gray.500">Cofradías</Stat.Label>
                  <Stat.ValueText fontSize="3xl" color="green.500">
                    {stats?.totalCofradias || 0}
                  </Stat.ValueText>
                  <Stat.HelpText>
                    <Icon mr={1} asChild><FaChurch /></Icon>
                    {stats?.verifiedCofradias || 0} verificadas
                  </Stat.HelpText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
          
          <GridItem>
            <Card.Root bg={cardBg} borderColor={stats?.pendingVerifications > 0 ? 'orange.300' : borderColor} borderWidth={stats?.pendingVerifications > 0 ? '2px' : '1px'}>
              <Card.Body>
                <Stat.Root>
                  <Stat.Label color="gray.500">Pendientes de Validar</Stat.Label>
                  <Stat.ValueText fontSize="3xl" color={stats?.pendingVerifications > 0 ? 'orange.500' : 'gray.400'}>
                    {stats?.pendingVerifications || 0}
                  </Stat.ValueText>
                  <Stat.HelpText>
                    <Icon mr={1} asChild><FaClock /></Icon>
                    Solicitudes activas
                  </Stat.HelpText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          </GridItem>
        </Grid>

        {/* Main Content Tabs */}
        <Card.Root bg={cardBg}>
          <Card.Body p={0}>
            <Tabs.Root colorPalette="purple" lazyMount>
              <Tabs.List px={6} pt={4}>
                <Tab value="0">
                  <HStack>
                    <Icon asChild><FaClock /></Icon>
                    <Text>Pendientes</Text>
                    {pendingVerifications.length > 0 && (
                      <Badge colorPalette="orange" ml={2}>{pendingVerifications.length}</Badge>
                    )}
                  </HStack>
                </Tab>
                <Tab value="1">
                  <HStack>
                    <Icon asChild><FaCheckCircle /></Icon>
                    <Text>Historial</Text>
                  </HStack>
                </Tab>
                <Tab value="2">
                  <HStack>
                    <Icon asChild><FaUsers /></Icon>
                    <Text>Usuarios</Text>
                  </HStack>
                </Tab>
                <Tab value="3">
                  <HStack>
                    <Icon asChild><FaEuroSign /></Icon>
                    <Text>Planes y costes</Text>
                  </HStack>
                </Tab>
              </Tabs.List>

              <TabPanels>
                {/* Pending Validations Tab */}
                <TabPanel value="0" p={6}>
                  <VStack gap={6} align="stretch">
                    {/* Search by code */}
                    <Card.Root variant="outline" borderColor="purple.200" bg="purple.50">
                      <Card.Body>
                        <Text fontSize="sm" fontWeight="600" color="purple.700" mb={3}>
                          Buscar solicitud por código
                        </Text>
                        <HStack>
                          <Input
                            placeholder="Introduce el código de 6 cifras..."
                            value={searchCode}
                            onChange={(e) => setSearchCode(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchByCode()}
                            maxLength={6}
                            fontFamily="mono"
                            letterSpacing="0.15em"
                            bg="white"
                            size="md"
                          />
                          <Button
                            colorPalette="purple"
                            onClick={handleSearchByCode}
                            loading={isSearching}
                            loadingText="Buscando..."
                            px={8}
                          >
                            Buscar
                          </Button>
                        </HStack>
                        {searchError && (
                          <Text fontSize="sm" color="red.500" mt={2}>{searchError}</Text>
                        )}
                      </Card.Body>
                    </Card.Root>

                    {/* Search result card */}
                    {searchResult && (
                      <Card.Root variant="outline" borderColor="green.300" borderWidth={2}>
                        <Card.Body>
                          <VStack gap={4} align="stretch">
                            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                              <HStack gap={4}>
                                <Avatar.Root
                                  size="lg"
                                  bg={searchResult.userType === 'BANDA' ? 'blue.500' : 'green.500'}><Avatar.Fallback name={searchResult.profile?.nombre} /><Avatar.Image src={searchResult.profile?.avatar} /></Avatar.Root>
                                <VStack align="start" gap={1}>
                                  <HStack>
                                    <Text fontWeight="bold" fontSize="lg">{searchResult.profile?.nombre || 'Sin nombre'}</Text>
                                    <Badge colorPalette={searchResult.userType === 'BANDA' ? 'blue' : 'green'}>
                                      {searchResult.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.500">CIF: {searchResult.cif}</Text>
                                  <Text fontSize="sm" color="gray.500">Email: {searchResult.user?.email}</Text>
                                  <Text fontSize="xs" color="gray.400">
                                    Solicitado: {formatDate(searchResult.createdAt)}
                                  </Text>
                                </VStack>
                              </HStack>
                              <Button
                                variant="outline"
                                colorPalette="purple"
                                size="sm"
                                onClick={() => handleViewDetails(searchResult)}><FaEye />Ver detalles completos
                                                              </Button>
                            </Flex>

                            <Separator />

                            {/* Profile preview */}
                            {searchResult.profile?.bio && (
                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Bio</Text>
                                <Text fontSize="sm" color="gray.600" lineClamp={3}>{searchResult.profile.bio}</Text>
                              </Box>
                            )}

                            <Separator />

                            {/* Validation action area */}
                            <Box bg="purple.50" rounded="lg" p={4}>
                              <Text fontSize="sm" fontWeight="600" color="purple.700" mb={3}>
                                Validar perfil
                              </Text>
                              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={3} alignItems="end">
                                <Box>
                                  <Text fontSize="xs" color="gray.500" mb={1}>Código coincidente</Text>
                                  <Text fontSize="lg" fontWeight="bold" color="green.600" letterSpacing="0.2em" fontFamily="mono">
                                    {searchResult.verificationCode}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color="gray.500" mb={1}>Red social</Text>
                                  <NativeSelect.Root>
                                    <NativeSelect.Field
                                      size="sm"
                                      placeholder="Selecciona..."
                                      value={adminSocialNetwork}
                                      onChange={(e) => setAdminSocialNetwork(e.target.value)}
                                      bg="white">
                                      <option value="instagram">Instagram</option>
                                      <option value="facebook">Facebook</option>
                                      <option value="tiktok">TikTok</option>
                                      <option value="twitter">X</option>
                                      <option value="youtube">YouTube</option>
                                    </NativeSelect.Field>
                                    <NativeSelect.Indicator />
                                  </NativeSelect.Root>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color="gray.500" mb={1}>Username del que envió el DM</Text>
                                  <Input
                                    size="sm"
                                    placeholder="@usuario"
                                    value={adminSocialUsername}
                                    onChange={(e) => setAdminSocialUsername(e.target.value)}
                                    bg="white"
                                  />
                                </Box>
                              </Grid>
                              <Text fontSize="xs" color="gray.400" mt={2}>
                                Este username se asignará como nombre de usuario en Cadencya
                              </Text>
                            </Box>

                            {/* Action buttons */}
                            <Flex justify="flex-end" gap={3}>
                              <Button
                                size="sm"
                                colorPalette="red"
                                variant="outline"
                                onClick={() => handleProcessVerification('REJECT')}
                                loading={isProcessing}
                                loadingText="Rechazando..."
                              >
                                Rechazar
                              </Button>
                              <Button
                                size="md"
                                colorPalette="green"
                                onClick={() => {
                                  handleProcessVerification('APPROVE', searchResult.verificationCode);
                                }}
                                loading={isProcessing}
                                loadingText="Validando..."
                              >
                                Validar Perfil
                              </Button>
                            </Flex>
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Simple list of all pending requests */}
                    <Separator />
                    <Heading size="sm" color="gray.600">
                      Todas las solicitudes pendientes ({pendingVerifications.length})
                    </Heading>
                    
                    {pendingVerifications.length === 0 ? (
                      <Alert.Root status="info" borderRadius="lg">
                        <Alert.Indicator />
                        <Alert.Title>No hay solicitudes pendientes</Alert.Title>
                        <Alert.Description>
                          Todas las solicitudes de validación han sido procesadas.
                        </Alert.Description>
                      </Alert.Root>
                    ) : (
                      <Box overflowX="auto">
                        <Table.Root variant="simple" size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Perfil</Table.ColumnHeader>
                              <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                              <Table.ColumnHeader>Código</Table.ColumnHeader>
                              <Table.ColumnHeader>Fecha</Table.ColumnHeader>
                              <Table.ColumnHeader></Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {pendingVerifications.map((request) => (
                              <Table.Row key={request.id}>
                                <Table.Cell>
                                  <HStack>
                                    <Avatar.Root size="xs"><Avatar.Fallback name={request.profile?.nombre} /><Avatar.Image src={request.profile?.avatar} /></Avatar.Root>
                                    <Text fontSize="sm">{request.profile?.nombre || 'Sin nombre'}</Text>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge size="sm" colorPalette={request.userType === 'BANDA' ? 'blue' : 'green'}>
                                    {request.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontFamily="mono" fontSize="sm" letterSpacing="0.1em">
                                    {request.verificationCode}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell fontSize="xs" color="gray.500">{formatDate(request.createdAt)}</Table.Cell>
                                <Table.Cell>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="purple"
                                    onClick={() => {
                                      setSearchCode(request.verificationCode);
                                      setSearchResult(null);
                                      setSearchError('');
                                      // Trigger search with this code
                                      setSearchCode(request.verificationCode);
                                      setTimeout(async () => {
                                        try {
                                          setIsSearching(true);
                                          const response = await adminAPI.searchByCode(request.verificationCode);
                                          setSearchResult(response.data);
                                          setSelectedRequest(response.data);
                                          setAdminVerificationCode('');
                                          setAdminSocialUsername('');
                                          setAdminSocialNetwork('');
                                        } catch (err) {
                                          setSearchError(err.response?.data?.message || 'Error');
                                        } finally {
                                          setIsSearching(false);
                                        }
                                      }, 0);
                                    }}
                                  >
                                    Revisar
                                  </Button>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* History Tab */}
                <TabPanel value="1" p={0}>
                  <Box overflowX="auto">
                    <Table.Root variant="simple">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Usuario</Table.ColumnHeader>
                          <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                          <Table.ColumnHeader>CIF</Table.ColumnHeader>
                          <Table.ColumnHeader>Estado</Table.ColumnHeader>
                          <Table.ColumnHeader>Validado vía</Table.ColumnHeader>
                          <Table.ColumnHeader>Fecha Solicitud</Table.ColumnHeader>
                          <Table.ColumnHeader>Fecha Proceso</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {allVerifications.map((request) => (
                          <Table.Row key={request.id}>
                            <Table.Cell>
                              <HStack>
                                <Avatar.Root size="sm"><Avatar.Fallback name={request.profile?.nombre} /><Avatar.Image src={request.profile?.avatar} /></Avatar.Root>
                                <Text>{request.profile?.nombre || 'Sin nombre'}</Text>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette={request.userType === 'BANDA' ? 'blue' : 'green'}>
                                {request.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>{request.cif}</Table.Cell>
                            <Table.Cell>{getStatusBadge(request.status)}</Table.Cell>
                            <Table.Cell fontSize="sm">
                              {request.socialUsername ? (
                                <VStack align="start" gap={0}>
                                  <Text fontWeight="medium">@{request.socialUsername}</Text>
                                  <Text fontSize="xs" color="gray.500">{labelSocialNetwork(request.socialNetwork)}</Text>
                                </VStack>
                              ) : '-'}
                            </Table.Cell>
                            <Table.Cell fontSize="sm">{formatDate(request.createdAt)}</Table.Cell>
                            <Table.Cell fontSize="sm">
                              {request.processedAt ? formatDate(request.processedAt) : '-'}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </TabPanel>

                {/* Users Tab */}
                <TabPanel value="2" p={0}>
                  <Box overflowX="auto">
                    <Table.Root variant="simple">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Usuario</Table.ColumnHeader>
                          <Table.ColumnHeader>Email</Table.ColumnHeader>
                          <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                          <Table.ColumnHeader>Ubicación</Table.ColumnHeader>
                          <Table.ColumnHeader>Estado Validación</Table.ColumnHeader>
                          <Table.ColumnHeader>Fecha Registro</Table.ColumnHeader>
                          <Table.ColumnHeader></Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {allUsers.map((user) => (
                          <Table.Row key={user.id}>
                            <Table.Cell>
                              <HStack>
                                <Avatar.Root size="sm"><Avatar.Fallback name={user.nombre} /><Avatar.Image src={user.avatar} /></Avatar.Root>
                                <Text>{user.nombre}</Text>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell>{user.email}</Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette={user.userType === 'BANDA' ? 'blue' : 'green'}>
                                {user.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell fontSize="sm">
                              {user.localizacion?.ciudad || '-'}, {user.localizacion?.provincia || '-'}
                            </Table.Cell>
                            <Table.Cell>{getStatusBadge(user.verificationStatus)}</Table.Cell>
                            <Table.Cell fontSize="sm">{formatDate(user.createdAt)}</Table.Cell>
                            <Table.Cell>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="purple"
                                onClick={() => handleViewUserProfile(user)}><FaEye />Ver perfil
                                                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </TabPanel>

                {/* Planes y costes Tab */}
                <TabPanel value="3" p={0}>
                  <AdminPlanesHub />
                </TabPanel>
              </TabPanels>
            </Tabs.Root>
          </Card.Body>
        </Card.Root>
      </Container>

      {/* Verification Details Modal */}
      <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack>
                  <Icon color="purple.500" asChild><FaShieldAlt /></Icon>
                  <Text>Revisar Solicitud de Validación</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {selectedRequest && (
                  <VStack gap={6} align="stretch">
                    {/* Profile Info */}
                    <Card.Root variant="outline">
                      <Card.Body>
                        <HStack gap={4}>
                          <Avatar.Root size="xl"><Avatar.Fallback name={selectedRequest.profile?.nombre} /><Avatar.Image src={selectedRequest.profile?.avatar} /></Avatar.Root>
                          <VStack align="start" gap={1}>
                            <Heading size="md">{selectedRequest.profile?.nombre}</Heading>
                            <Badge colorPalette={selectedRequest.userType === 'BANDA' ? 'blue' : 'green'}>
                              {selectedRequest.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                            </Badge>
                            <Text fontSize="sm" color="gray.500">
                              {selectedRequest.user?.email}
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                              Registrado: {formatDate(selectedRequest.user?.createdAt)}
                            </Text>
                          </VStack>
                        </HStack>
                      </Card.Body>
                    </Card.Root>

                    {/* Información del Perfil */}
                    <Card.Root variant="outline">
                      <Card.Header pb={2}>
                        <Heading size="sm">Información del Perfil</Heading>
                      </Card.Header>
                      <Card.Body pt={2}>
                        <VStack align="stretch" gap={4}>
                          {/* Bio */}
                          {selectedRequest.profile?.bio && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Descripción</Text>
                              <Text fontSize="sm" mt={1}>{selectedRequest.profile.bio}</Text>
                            </Box>
                          )}

                          {/* Grid de información general */}
                          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                            {selectedRequest.userType === 'BANDA' && (
                              <>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Tipo de Agrupación</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.tipoGrupo || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Año de Fundación</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.anoFundacion || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Número de Músicos</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.nMusicos || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Km Dispuestos a Recorrer</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.kmDispuestos || 'No especificado'}</Text>
                                </Box>
                              </>
                            )}

                            {selectedRequest.userType === 'COFRADIA' && (
                              <>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Tipo de Agrupación</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.tipoGrupo || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Fecha de Fundación</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.fechaFundacion || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Número de Hermanos</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.numeroHermanos || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Patrono</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.patrono || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Sede Canónica</Text>
                                  <Text fontWeight="medium">{selectedRequest.profile?.sedeCanonica || 'No especificado'}</Text>
                                </Box>
                              </>
                            )}
                          </Grid>

                          {/* Localización */}
                          {selectedRequest.profile?.localizacion && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Localización</Text>
                              <Text fontSize="sm" mt={1}>
                                {selectedRequest.profile.localizacion.calle && `${selectedRequest.profile.localizacion.calle}, `}
                                {selectedRequest.profile.localizacion.ciudad && `${selectedRequest.profile.localizacion.ciudad}, `}
                                {selectedRequest.profile.localizacion.provincia && `${selectedRequest.profile.localizacion.provincia} `}
                                {selectedRequest.profile.localizacion.codigoPostal && `(${selectedRequest.profile.localizacion.codigoPostal})`}
                              </Text>
                              {selectedRequest.profile?.lat && selectedRequest.profile?.lng && (
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                  Coordenadas: {Number(selectedRequest.profile.lat).toFixed(6)}, {Number(selectedRequest.profile.lng).toFixed(6)}
                                </Text>
                              )}
                            </Box>
                          )}

                          {/* Contacto Público */}
                          {selectedRequest.profile?.contactoPublico && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Contacto Público</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedRequest.profile.contactoPublico.telefono && (
                                  <Text fontSize="sm">📞 {selectedRequest.profile.contactoPublico.telefono}</Text>
                                )}
                                {selectedRequest.profile.contactoPublico.email && (
                                  <Text fontSize="sm">✉️ {selectedRequest.profile.contactoPublico.email}</Text>
                                )}
                              </VStack>
                            </Box>
                          )}

                          {/* Redes Sociales */}
                          {selectedRequest.profile?.redesSociales && Object.keys(selectedRequest.profile.redesSociales).some(key => selectedRequest.profile.redesSociales[key]) && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Redes Sociales</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedRequest.profile.redesSociales.facebook && (
                                  <Text fontSize="sm">Facebook: {selectedRequest.profile.redesSociales.facebook}</Text>
                                )}
                                {selectedRequest.profile.redesSociales.instagram && (
                                  <Text fontSize="sm">Instagram: {selectedRequest.profile.redesSociales.instagram}</Text>
                                )}
                                {selectedRequest.profile.redesSociales.twitter && (
                                  <Text fontSize="sm">X: {selectedRequest.profile.redesSociales.twitter}</Text>
                                )}
                                {selectedRequest.profile.redesSociales.youtube && (
                                  <Text fontSize="sm">YouTube: {selectedRequest.profile.redesSociales.youtube}</Text>
                                )}
                                {selectedRequest.profile.redesSociales.web && (
                                  <Text fontSize="sm">Web: {selectedRequest.profile.redesSociales.web}</Text>
                                )}
                              </VStack>
                            </Box>
                          )}

                          {/* Especialidades musicales (solo bandas) */}
                          {selectedRequest.userType === 'BANDA' && selectedRequest.profile?.especialidades?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Especialidades Musicales</Text>
                              <Flex wrap="wrap" gap={2} mt={1}>
                                {selectedRequest.profile.especialidades.map((esp, idx) => (
                                  <Badge key={idx} colorPalette="blue">{esp}</Badge>
                                ))}
                              </Flex>
                            </Box>
                          )}

                          {/* Fotos de la galería */}
                          {selectedRequest.profile?.fotos?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600" mb={2}>Galería de Fotos ({selectedRequest.profile.fotos.length})</Text>
                              <Grid templateColumns="repeat(auto-fill, minmax(100px, 1fr))" gap={2}>
                                {selectedRequest.profile.fotos.map((foto, idx) => (
                                  <Image
                                    key={idx}
                                    src={foto}
                                    alt={`Foto ${idx + 1}`}
                                    borderRadius="md"
                                    objectFit="cover"
                                    h="100px"
                                    w="100%"
                                    cursor="pointer"
                                    onClick={() => window.open(foto, '_blank')}
                                  />
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {/* Videos */}
                          {selectedRequest.profile?.videos?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Videos ({selectedRequest.profile.videos.length})</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedRequest.profile.videos.map((video, idx) => (
                                  <Link
                                    key={idx}
                                    href={video}
                                    color="blue.500"
                                    fontSize="sm"
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    Video {idx + 1} →
                                  </Link>
                                ))}
                              </VStack>
                            </Box>
                          )}

                          {/* Información específica de cofradías */}
                          {selectedRequest.userType === 'COFRADIA' && (
                            <>
                              {selectedRequest.profile?.horariosCulto && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Horarios de Culto</Text>
                                  <Text fontSize="sm" mt={1}>{selectedRequest.profile.horariosCulto}</Text>
                                </Box>
                              )}
                              {selectedRequest.profile?.procesionesAnuales && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Procesiones Anuales</Text>
                                  <Text fontSize="sm" mt={1}>{selectedRequest.profile.procesionesAnuales}</Text>
                                </Box>
                              )}
                              {selectedRequest.profile?.patrimonioArtistico && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Patrimonio Artístico</Text>
                                  <Text fontSize="sm" mt={1}>{selectedRequest.profile.patrimonioArtistico}</Text>
                                </Box>
                              )}
                              {selectedRequest.profile?.actividadesYCultos && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Actividades y Cultos</Text>
                                  <Text fontSize="sm" mt={1}>{selectedRequest.profile.actividadesYCultos}</Text>
                                </Box>
                              )}
                            </>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    {/* Legal Info */}
                    <Card.Root variant="outline">
                      <Card.Header pb={2}>
                        <Heading size="sm">Información Legal</Heading>
                      </Card.Header>
                      <Card.Body pt={2}>
                        <Grid templateColumns="1fr 1fr" gap={4}>
                          <Box>
                            <Text fontSize="sm" color="gray.500">CIF</Text>
                            <Text fontWeight="medium">{selectedRequest.cif}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Fecha Solicitud</Text>
                            <Text fontWeight="medium">{formatDate(selectedRequest.createdAt)}</Text>
                          </Box>
                        </Grid>
                      </Card.Body>
                    </Card.Root>

                    {/* Persona Responsable */}
                    <Card.Root variant="outline">
                      <Card.Header pb={2}>
                        <Heading size="sm">Persona Responsable</Heading>
                      </Card.Header>
                      <Card.Body pt={2}>
                        <Grid templateColumns="1fr 1fr" gap={4}>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Nombre Completo</Text>
                            <Text fontWeight="medium">
                              {selectedRequest.personaResponsable?.nombre} {selectedRequest.personaResponsable?.apellidos}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">DNI</Text>
                            <Text fontWeight="medium">{selectedRequest.personaResponsable?.dni}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Teléfono</Text>
                            <Text fontWeight="medium">{selectedRequest.personaResponsable?.telefono}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Email</Text>
                            <Text fontWeight="medium">{selectedRequest.personaResponsable?.email}</Text>
                          </Box>
                        </Grid>
                      </Card.Body>
                    </Card.Root>

                    {/* Documentos */}
                    {selectedRequest.documentos?.length > 0 && (
                      <Card.Root variant="outline">
                        <Card.Header pb={2}>
                          <Heading size="sm">Documentos Adjuntos</Heading>
                        </Card.Header>
                        <Card.Body pt={2}>
                          <VStack align="stretch" gap={2}>
                            {selectedRequest.documentos.map((doc, idx) => (
                              <Button variant="outline" size="sm" justifyContent="start" asChild><a key={idx} href={doc} target="_blank">Documento {idx + 1}
                                </a></Button>
                            ))}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Notas */}
                    {selectedRequest.notas && (
                      <Card.Root variant="outline">
                        <Card.Header pb={2}>
                          <Heading size="sm">Notas del Solicitante</Heading>
                        </Card.Header>
                        <Card.Body pt={2}>
                          <Text>{selectedRequest.notas}</Text>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Info note */}
                    <Alert.Root status="info" rounded="lg" size="sm">
                      <Alert.Indicator />
                      <Text fontSize="sm">
                        Para validar o rechazar esta solicitud, usa el buscador por código en la pestaña de Pendientes.
                      </Text>
                    </Alert.Root>
                  </VStack>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="ghost" onClick={onClose}>
                  Cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* User Profile Modal */}
      <Dialog.Root open={isProfileOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onProfileClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack>
                  <Icon color="purple.500" asChild><FaEye /></Icon>
                  <Text>Perfil de Usuario</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {selectedUserProfile && (
                  <VStack gap={6} align="stretch">
                    {/* Profile header */}
                    <Card.Root variant="outline">
                      <Card.Body>
                        <HStack gap={4}>
                          <Avatar.Root size="xl"><Avatar.Fallback name={selectedUserProfile.profile?.nombre} /><Avatar.Image src={selectedUserProfile.profile?.avatar} /></Avatar.Root>
                          <VStack align="start" gap={1}>
                            <Heading size="md">{selectedUserProfile.profile?.nombre}</Heading>
                            <HStack>
                              <Badge colorPalette={selectedUserProfile.userType === 'BANDA' ? 'blue' : 'green'}>
                                {selectedUserProfile.userType === 'BANDA' ? 'Banda' : 'Cofradía'}
                              </Badge>
                              {getStatusBadge(selectedUserProfile.profile?.verificationStatus)}
                            </HStack>
                            <Text fontSize="sm" color="gray.500">
                              {selectedUserProfile.user?.email}
                            </Text>
                            {selectedUserProfile.user?.username && (
                              <Text fontSize="sm" color="purple.500" fontWeight="medium">
                                @{selectedUserProfile.user.username}
                              </Text>
                            )}
                            <Text fontSize="sm" color="gray.400">
                              Registrado: {formatDate(selectedUserProfile.user?.createdAt)}
                            </Text>
                          </VStack>
                        </HStack>
                      </Card.Body>
                    </Card.Root>

                    {/* Profile info */}
                    <Card.Root variant="outline">
                      <Card.Header pb={2}>
                        <Heading size="sm">Información del Perfil</Heading>
                      </Card.Header>
                      <Card.Body pt={2}>
                        <VStack align="stretch" gap={4}>
                          {selectedUserProfile.profile?.bio && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Descripción</Text>
                              <Text fontSize="sm" mt={1}>{selectedUserProfile.profile.bio}</Text>
                            </Box>
                          )}

                          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                            {selectedUserProfile.userType === 'BANDA' && (
                              <>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Tipo de Agrupación</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.tipoGrupo || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Año de Fundación</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.anoFundacion || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Número de Músicos</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.nMusicos || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Km Dispuestos a Recorrer</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.kmDispuestos || 'No especificado'}</Text>
                                </Box>
                              </>
                            )}

                            {selectedUserProfile.userType === 'COFRADIA' && (
                              <>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Tipo de Agrupación</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.tipoGrupo || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Fecha de Fundación</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.fechaFundacion || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Número de Hermanos</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.numeroHermanos || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Patrono</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.patrono || 'No especificado'}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" color="gray.500">Sede Canónica</Text>
                                  <Text fontWeight="medium">{selectedUserProfile.profile?.sedeCanonica || 'No especificado'}</Text>
                                </Box>
                              </>
                            )}
                          </Grid>

                          {selectedUserProfile.profile?.localizacion && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Localización</Text>
                              <Text fontSize="sm" mt={1}>
                                {selectedUserProfile.profile.localizacion.calle && `${selectedUserProfile.profile.localizacion.calle}, `}
                                {selectedUserProfile.profile.localizacion.ciudad && `${selectedUserProfile.profile.localizacion.ciudad}, `}
                                {selectedUserProfile.profile.localizacion.provincia && `${selectedUserProfile.profile.localizacion.provincia} `}
                                {selectedUserProfile.profile.localizacion.codigoPostal && `(${selectedUserProfile.profile.localizacion.codigoPostal})`}
                              </Text>
                              {selectedUserProfile.profile?.lat && selectedUserProfile.profile?.lng && (
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                  Coordenadas: {Number(selectedUserProfile.profile.lat).toFixed(6)}, {Number(selectedUserProfile.profile.lng).toFixed(6)}
                                </Text>
                              )}
                            </Box>
                          )}

                          {selectedUserProfile.profile?.contactoPublico && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Contacto Público</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedUserProfile.profile.contactoPublico.telefono && (
                                  <Text fontSize="sm">Tel: {selectedUserProfile.profile.contactoPublico.telefono}</Text>
                                )}
                                {selectedUserProfile.profile.contactoPublico.email && (
                                  <Text fontSize="sm">Email: {selectedUserProfile.profile.contactoPublico.email}</Text>
                                )}
                              </VStack>
                            </Box>
                          )}

                          {selectedUserProfile.profile?.redesSociales && Object.keys(selectedUserProfile.profile.redesSociales).some(key => selectedUserProfile.profile.redesSociales[key]) && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Redes Sociales</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedUserProfile.profile.redesSociales.facebook && (
                                  <Text fontSize="sm">Facebook: {selectedUserProfile.profile.redesSociales.facebook}</Text>
                                )}
                                {selectedUserProfile.profile.redesSociales.instagram && (
                                  <Text fontSize="sm">Instagram: {selectedUserProfile.profile.redesSociales.instagram}</Text>
                                )}
                                {selectedUserProfile.profile.redesSociales.twitter && (
                                  <Text fontSize="sm">X: {selectedUserProfile.profile.redesSociales.twitter}</Text>
                                )}
                                {selectedUserProfile.profile.redesSociales.youtube && (
                                  <Text fontSize="sm">YouTube: {selectedUserProfile.profile.redesSociales.youtube}</Text>
                                )}
                                {selectedUserProfile.profile.redesSociales.web && (
                                  <Text fontSize="sm">Web: {selectedUserProfile.profile.redesSociales.web}</Text>
                                )}
                              </VStack>
                            </Box>
                          )}

                          {selectedUserProfile.userType === 'BANDA' && selectedUserProfile.profile?.especialidades?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Especialidades Musicales</Text>
                              <Flex wrap="wrap" gap={2} mt={1}>
                                {selectedUserProfile.profile.especialidades.map((esp, idx) => (
                                  <Badge key={idx} colorPalette="blue">{esp}</Badge>
                                ))}
                              </Flex>
                            </Box>
                          )}

                          {selectedUserProfile.profile?.fotos?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600" mb={2}>Galería de Fotos ({selectedUserProfile.profile.fotos.length})</Text>
                              <Grid templateColumns="repeat(auto-fill, minmax(100px, 1fr))" gap={2}>
                                {selectedUserProfile.profile.fotos.map((foto, idx) => (
                                  <Image
                                    key={idx}
                                    src={foto}
                                    alt={`Foto ${idx + 1}`}
                                    borderRadius="md"
                                    objectFit="cover"
                                    h="100px"
                                    w="100%"
                                    cursor="pointer"
                                    onClick={() => window.open(foto, '_blank')}
                                  />
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {selectedUserProfile.profile?.videos?.length > 0 && (
                            <Box>
                              <Text fontSize="sm" color="gray.500" fontWeight="600">Videos ({selectedUserProfile.profile.videos.length})</Text>
                              <VStack align="stretch" gap={1} mt={1}>
                                {selectedUserProfile.profile.videos.map((video, idx) => (
                                  <Link
                                    key={idx}
                                    href={typeof video === 'object' ? video.url : video}
                                    color="blue.500"
                                    fontSize="sm"
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    {typeof video === 'object' && video.titulo ? video.titulo : `Video ${idx + 1}`} →
                                  </Link>
                                ))}
                              </VStack>
                            </Box>
                          )}

                          {selectedUserProfile.userType === 'COFRADIA' && (
                            <>
                              {selectedUserProfile.profile?.horariosCulto && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Horarios de Culto</Text>
                                  <Text fontSize="sm" mt={1}>{selectedUserProfile.profile.horariosCulto}</Text>
                                </Box>
                              )}
                              {selectedUserProfile.profile?.procesionesAnuales && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Procesiones Anuales</Text>
                                  <Text fontSize="sm" mt={1}>{selectedUserProfile.profile.procesionesAnuales}</Text>
                                </Box>
                              )}
                              {selectedUserProfile.profile?.patrimonioArtistico && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Patrimonio Artístico</Text>
                                  <Text fontSize="sm" mt={1}>{selectedUserProfile.profile.patrimonioArtistico}</Text>
                                </Box>
                              )}
                              {selectedUserProfile.profile?.actividadesYCultos?.length > 0 && (
                                <Box>
                                  <Text fontSize="sm" color="gray.500" fontWeight="600">Actividades y Cultos</Text>
                                  <VStack align="stretch" gap={1} mt={1}>
                                    {selectedUserProfile.profile.actividadesYCultos.map((act, idx) => (
                                      <Text key={idx} fontSize="sm">{act}</Text>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                            </>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" onClick={onProfileClose}>
                  Cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default AdminDashboard;






